import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function YesNoBadge({ value, yesLabel = 'Yes', noLabel = 'No' }) {
  const yes = Boolean(value);
  return (
    <Badge
      variant="outline"
      className={yes ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-green-50 text-green-800 border-green-200'}
    >
      {yes ? yesLabel : noLabel}
    </Badge>
  );
}

function ReadOnlyRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600 shrink-0">{label}</span>
      <div className="text-sm text-gray-900 text-right">{children}</div>
    </div>
  );
}

function AttestationSection({ title, children }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">{title}</h4>
      <div>{children}</div>
    </div>
  );
}

export default function ReviewDeclarationsDialog({
  open,
  onClose,
  employeeId,
  alreadyVerified = false,
  verifiedAt = null,
  verifiedByName = null,
  onSuccess,
  onRequestAmend,
}) {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open || !employeeId) return;
    setReviewNotes('');
    setIsFetching(true);
    axios
      .get(`${API}/employees/${employeeId}/declarations/applicant-attestation`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setPayload(res.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Could not load applicant declarations');
        setPayload(null);
      })
      .finally(() => setIsFetching(false));
  }, [open, employeeId, token]);

  const att = payload?.attestation || {};
  const criminal = att.criminal || {};
  const health = att.health || {};
  const rtw = att.right_to_work || {};
  const driving = att.driving || {};
  const consents = att.consents || {};

  const handleConfirmVerify = async () => {
    setIsLoading(true);
    try {
      // Read-only attestation may be recoverable from the submitted application
      // before the employee review snapshot has applicant-origin provenance.
      // This operation only reconciles a real tenant-scoped submission;
      // it does NOT approve or alter the applicant's declaration answers.
      await axios.post(
        `${API}/employees/${employeeId}/declarations/reconcile-source`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await axios.post(
        `${API}/employees/${employeeId}/declarations/review`,
        { review_notes: reviewNotes.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Declarations reviewed');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string' && detail.trim()
          ? detail
          : 'The application is visible, but the review could not be saved. Your applicant did not need to resubmit.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-600" />
            {alreadyVerified ? 'Applicant declarations' : 'Review applicant declarations'}
          </DialogTitle>
          <DialogDescription>
            {alreadyVerified
              ? 'Read-only view of what the applicant declared on their online application.'
              : 'This is what the applicant submitted. Confirm you have checked it before moving the review forward.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto pr-1 flex-1">
          {alreadyVerified && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-900 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span>
                Declarations reviewed
                {verifiedByName ? <> by {verifiedByName}</> : null}
                {verifiedAt ? <> · {new Date(verifiedAt).toLocaleString('en-GB')}</> : null}
              </span>
            </div>
          )}
          {isFetching ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading applicant declarations…
            </div>
          ) : payload ? (
            <>
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                Source: online application
                {payload.submitted_at ? (
                  <> · Submitted {new Date(payload.submitted_at).toLocaleString('en-GB')}</>
                ) : null}
                {payload.warning ? <> · {payload.warning}</> : null}
              </div>

              <AttestationSection title="Criminal record declaration">
                <ReadOnlyRow label="Unspent convictions/cautions">
                  <YesNoBadge value={criminal.has_convictions} yesLabel="Declared" noLabel="None declared" />
                </ReadOnlyRow>
                {criminal.has_convictions && criminal.conviction_details ? (
                  <ReadOnlyRow label="Details">{criminal.conviction_details}</ReadOnlyRow>
                ) : null}
                <ReadOnlyRow label="Enhanced DBS consent">
                  <YesNoBadge value={criminal.consents_to_dbs_check} yesLabel="Consent given" noLabel="Not given" />
                </ReadOnlyRow>
              </AttestationSection>

              <AttestationSection title="Health screening (application)">
                <ReadOnlyRow label="Can perform physical care tasks">
                  <YesNoBadge value={health.can_perform_physical_tasks} />
                </ReadOnlyRow>
                <ReadOnlyRow label="Condition affecting work">
                  <YesNoBadge
                    value={health.has_condition_affecting_work || health.summary_has_health_conditions}
                    yesLabel="Declared"
                    noLabel="None declared"
                  />
                </ReadOnlyRow>
                {(health.condition_details || health.summary_details) ? (
                  <ReadOnlyRow label="Details">{health.condition_details || health.summary_details}</ReadOnlyRow>
                ) : null}
                <ReadOnlyRow label="Reasonable adjustments needed">
                  <YesNoBadge value={health.requires_reasonable_adjustments} yesLabel="Yes" noLabel="No" />
                </ReadOnlyRow>
                {health.adjustment_details ? (
                  <ReadOnlyRow label="Adjustment details">{health.adjustment_details}</ReadOnlyRow>
                ) : null}
              </AttestationSection>

              <AttestationSection title="Right to work">
                <ReadOnlyRow label="Citizenship / status">{rtw.citizenship_status || '—'}</ReadOnlyRow>
                <ReadOnlyRow label="Work restrictions">
                  <YesNoBadge value={rtw.has_restrictions} yesLabel="Restrictions apply" noLabel="No restrictions" />
                </ReadOnlyRow>
                {rtw.visa_type ? <ReadOnlyRow label="Visa type">{rtw.visa_type}</ReadOnlyRow> : null}
                {rtw.visa_expiry ? <ReadOnlyRow label="Visa expiry">{rtw.visa_expiry}</ReadOnlyRow> : null}
              </AttestationSection>

              <AttestationSection title="Driving">
                <ReadOnlyRow label="UK driving licence">
                  <YesNoBadge value={driving.has_driving_licence} yesLabel="Yes" noLabel="No" />
                </ReadOnlyRow>
              </AttestationSection>

              <AttestationSection title="Application consents">
                <ReadOnlyRow label="Information accurate">
                  <YesNoBadge value={consents.information_accurate} yesLabel="Confirmed" noLabel="—" />
                </ReadOnlyRow>
                <ReadOnlyRow label="Reference checks">
                  <YesNoBadge value={consents.consents_to_reference_checks} yesLabel="Consented" noLabel="—" />
                </ReadOnlyRow>
                <ReadOnlyRow label="Background checks">
                  <YesNoBadge value={consents.consents_to_background_checks} yesLabel="Consented" noLabel="—" />
                </ReadOnlyRow>
              </AttestationSection>

              {!alreadyVerified && (
                <div className="space-y-1">
                  <Label className="text-xs text-gray-600">Review notes (optional)</Label>
                  <Textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="e.g. Checked and accepted."
                    className="min-h-[72px] text-sm"
                  />
                </div>
              )}

              {onRequestAmend ? (
                <p className="text-xs text-gray-500">
                  Spotted an error that does not match the application?{' '}
                  <button
                    type="button"
                    className="text-amber-700 underline hover:text-amber-900"
                    onClick={() => {
                      onClose();
                      onRequestAmend();
                    }}
                  >
                    Correct declaration error
                  </button>
                  {' '}(requires reason — only for genuine corrections).
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-gray-500 py-6 text-center">No applicant declaration data available.</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {alreadyVerified ? (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleConfirmVerify} disabled={isLoading || isFetching || !payload}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    I have reviewed these declarations
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
